// See: backend/docs/driver-scheduler.md §11 — GET /schedule + PATCH /lock
//
// Günlük sürücü programı (Gantt). Operatör:
//   - Tarih seçer → backend'den schedule çeker
//   - Trip block'larda kilit ikonuna tıklayarak lock/unlock eder
//   - Atanmamış servisler istatistiklerin hemen altında (Gantt'tan önce) listelenir

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";

import useDriverSchedulerApi from "./hooks/useDriverSchedulerApi";
import DriverTimelineRow from "./components/DriverTimelineRow";
import OptimizeDayModal from "./OptimizeDayModal";
import UncomputedServicesModal from "./UncomputedServicesModal";
import { tripKindLabel } from "./scheduleLabels";

import "./DailySchedule.css";

/** Gün sınırları — grid bu aralığın dışına taşmaz. */
const DAY_MIN = 0; // 00:00
const DAY_MAX = 23 * 60 + 59; // 23:59

/** Hiç bacak yokken Gantt için varsayılan pencere. */
const DEFAULT_GRID_START_MIN = 6 * 60;
const DEFAULT_GRID_END_MIN = 22 * 60;

const hhmmToMinutes = (s) => {
  if (!s || typeof s !== "string") return null;
  const [h, m] = s.split(":").map((p) => parseInt(p, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

/**
 * Tüm bacaklardan (atanmış + sürücüsüz) dinamik zaman penceresi üretir.
 * İlk bacak başlangıcı → son bacak bitişi; 00:00–23:59 ile sınırlanır.
 */
function computeGridBounds(allTrips) {
  let minStart = Infinity;
  let maxEnd = -Infinity;

  for (const trip of allTrips) {
    const start = hhmmToMinutes(trip?.startTime);
    const end = hhmmToMinutes(trip?.endTime);
    if (start !== null) minStart = Math.min(minStart, start);
    if (end !== null) maxEnd = Math.max(maxEnd, end);
  }

  if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd)) {
    return {
      gridStartMin: DEFAULT_GRID_START_MIN,
      gridEndMin: DEFAULT_GRID_END_MIN,
    };
  }

  const gridStartMin = Math.max(DAY_MIN, Math.min(minStart, DAY_MAX));
  let gridEndMin = Math.max(DAY_MIN, Math.min(maxEnd, DAY_MAX));
  if (gridEndMin <= gridStartMin) {
    gridEndMin = Math.min(DAY_MAX, gridStartMin + 30);
  }

  return { gridStartMin, gridEndMin };
}

function buildHourTicks(gridStartMin, gridEndMin) {
  const span = gridEndMin - gridStartMin;
  if (span <= 0) return [Math.floor(gridStartMin / 60)];

  const firstHour = Math.floor(gridStartMin / 60);
  const lastHour = Math.min(23, Math.ceil(gridEndMin / 60));
  const hours = [];
  for (let h = firstHour; h <= lastHour; h++) {
    hours.push(h);
  }
  return hours.length > 0 ? hours : [firstHour];
}

const KIND_LEGEND = [
  { kind: "SHIFT_PERSONEL", bg: "#1976d2", border: "#0d47a1" },
  { kind: "TRANSFER", bg: "#ef6c00", border: "#b53d00" },
  { kind: "STANDARD_PERSONEL", bg: "#90a4ae", border: "#546e7a" },
  { kind: "VEHICLE_ROUTE", bg: "#388e3c", border: "#1b5e20" },
];

const todayYYYYMMDD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const DailySchedule = () => {
  const { t } = useTranslation();
  const { isLoading, getDailySchedule, setLock } = useDriverSchedulerApi();

  const [date, setDate] = useState(todayYYYYMMDD());
  const [data, setData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [busyTripId, setBusyTripId] = useState(null);
  const [optimizeOpen, setOptimizeOpen] = useState(false);
  const [uncomputedOpen, setUncomputedOpen] = useState(false);

  const fetchSchedule = useCallback(async () => {
    setErrorMessage(null);
    try {
      const result = await getDailySchedule(date);
      setData(result);
    } catch (err) {
      setErrorMessage(err?.message || t("driverScheduler.fetchError"));
    }
  }, [date, getDailySchedule, t]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const handleToggleLock = useCallback(
    async (trip, nextLocked) => {
      if (!trip?.serviceId) return;
      setBusyTripId(trip.serviceId);
      try {
        const updated = await setLock(trip.serviceId, nextLocked);
        if (!updated) {
          throw new Error(t("driverScheduler.lockFailed"));
        }
        window?.toast?.success?.(nextLocked ? t("driverScheduler.lockedToast") : t("driverScheduler.unlockedToast"));
        // Optimistik update: data'da bu trip'in assignment.locked'ını güncelle
        setData((prev) => {
          if (!prev) return prev;
          const next = { ...prev };
          next.drivers = (prev.drivers || []).map((row) => ({
            ...row,
            trips: (row.trips || []).map((tr) =>
              tr.serviceId === trip.serviceId
                ? { ...tr, assignment: { ...(tr.assignment || {}), locked: !!nextLocked } }
                : tr,
            ),
          }));
          return next;
        });
      } catch (err) {
        window?.toast?.error?.(err?.message || t("driverScheduler.lockFailed"));
      } finally {
        setBusyTripId(null);
      }
    },
    [setLock, t],
  );

  const driverRows = data?.drivers || [];

  const totals = useMemo(() => {
    const program =
      data?.stats?.programTripCount ??
      data?.stats?.tripCount ??
      driverRows.reduce((acc, r) => acc + (r.trips?.length || 0), 0);

    return {
      drivers: data?.stats?.driverCount ?? driverRows.length,
      program,
      uncomputed: data?.stats?.uncomputedCount ?? (data?.uncomputed?.length || 0),
    };
  }, [data, driverRows]);

  const { gridStartMin, gridEndMin, hours } = useMemo(() => {
    const assignedTrips = driverRows.flatMap((r) => r.trips || []);
    const { gridStartMin: start, gridEndMin: end } = computeGridBounds(assignedTrips);
    return {
      gridStartMin: start,
      gridEndMin: end,
      hours: buildHourTicks(start, end),
    };
  }, [driverRows]);

  return (
    <div className="daily-schedule">
      <div className="daily-schedule__header">
        <h2>{t("driverScheduler.dailyScheduleTitle")}</h2>
        <div className="daily-schedule__controls">
          <label htmlFor="ds-date">{t("driverScheduler.date")}:</label>
          <input
            id="ds-date"
            type="date"
            className="daily-schedule__date-input"
            value={date}
            onChange={(e) => setDate(e.target.value || todayYYYYMMDD())}
          />
          {/* <Button
            type="button"
            onClick={() => setOptimizeOpen(true)}
            disabled={!date || isLoading}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <AutoFixHighIcon fontSize="small" />
            {t("driverScheduler.optimize.openButton")}
          </Button> */}
        </div>
      </div>

      <div className="daily-schedule__stats">
        <span className="daily-schedule__stat">
          <strong>{totals.drivers}</strong>
          {t("driverScheduler.activeDrivers")}
        </span>
        <span className="daily-schedule__stat">
          <strong>{totals.program}</strong>
          {t("driverScheduler.programTrips")}
        </span>
        <button
          type="button"
          className={`daily-schedule__stat daily-schedule__stat--clickable${totals.uncomputed > 0 ? " daily-schedule__stat--warn" : ""}`}
          disabled={totals.uncomputed === 0}
          onClick={() => setUncomputedOpen(true)}
          title={totals.uncomputed > 0 ? t("driverScheduler.uncomputedClickHint") : undefined}
        >
          <strong>{totals.uncomputed}</strong>
          {t("driverScheduler.uncomputedServices")}
        </button>
      </div>

      {isLoading && !data && (
        <div className="daily-schedule__loading">
          <LoadingSpinner />
          <span>{t("driverScheduler.scheduleLoading")}</span>
        </div>
      )}

      {errorMessage && (
        <div className="daily-schedule__empty" style={{ color: "#c62828" }}>
          {errorMessage}
        </div>
      )}

      {data && !errorMessage && (
        <>
          <div className="daily-schedule__gantt">
          {/* Saat şeridi */}
          <div className="daily-schedule__hour-bar">
            <div className="daily-schedule__hour-bar-spacer">{t("driverScheduler.driver")}</div>
            <div className="daily-schedule__hour-bar-grid">
              {hours.map((h, idx) => {
                if (hours.length > 2 && (idx === 0 || idx === hours.length - 1)) {
                  return null;
                }

                const span = gridEndMin - gridStartMin;
                const pct = span > 0 ? ((h * 60 - gridStartMin) / span) * 100 : 0;
                const isFirst = idx === 0;
                const isLast = idx === hours.length - 1;
                const tickEdge = isFirst ? "start" : isLast ? "end" : "middle";

                return (
                  <div
                    key={h}
                    className={`daily-schedule__hour-tick daily-schedule__hour-tick--${tickEdge}`}
                    style={{ left: `${pct}%` }}
                  >
                    {String(h).padStart(2, "0")}:00
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sürücü satırları */}
          <div className="daily-schedule__gantt-rows">
          {driverRows.length === 0 ? (
            <div className="daily-schedule__empty">{t("driverScheduler.noDrivers")}</div>
          ) : (
            driverRows.map((row) => (
              <DriverTimelineRow
                key={row.driver.id}
                row={row}
                gridStartMin={gridStartMin}
                gridEndMin={gridEndMin}
                hours={hours}
                onToggleLock={handleToggleLock}
                busyTripId={busyTripId}
                offLabel={t("driverScheduler.availability.offBadge")}
              />
            ))
          )}
          </div>
          </div>

          {/* Legend */}
          <div className="daily-schedule__legend">
            {KIND_LEGEND.map((k) => (
              <span key={k.kind} className="daily-schedule__legend-item">
                <span
                  className="daily-schedule__legend-swatch"
                  style={{ backgroundColor: k.bg, borderColor: k.border }}
                />
                {tripKindLabel(k.kind, t)}
              </span>
            ))}
          </div>
        </>
      )}

      {isLoading && data && <LoadingSpinner asOverlay />}

      <OptimizeDayModal
        show={optimizeOpen}
        onClose={() => setOptimizeOpen(false)}
        date={date}
        onApplied={() => {
          setOptimizeOpen(false);
          fetchSchedule();
        }}
      />

      <UncomputedServicesModal
        show={uncomputedOpen}
        onClose={() => setUncomputedOpen(false)}
        date={date}
        services={data?.uncomputed || []}
      />
    </div>
  );
};

export default DailySchedule;
